import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button,
  IconButton, Typography, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions,
  MenuItem, Chip, Switch, FormControlLabel,
  Card, CardContent, Grid, CircularProgress,
  Alert
} from '@mui/material';
import {
  Edit, Delete, Add, RestaurantMenu,
  Visibility, VisibilityOff, Warning
} from '@mui/icons-material';
import { categorieMenuAPI } from '../../../services/restaurantAPI';
import { toast } from 'react-toastify';

const CategoriesMenuPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    type_categorie: 'entree',
    description: '',
    est_active: true
  });

  const typeOptions = [
    { value: 'entree', label: 'Entrée' },
    { value: 'plat', label: 'Plat Principal' },
    { value: 'dessert', label: 'Dessert' },
    { value: 'accompagnement', label: 'Accompagnement' },
    { value: 'autre', label: 'Autre' },
  ];

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categorieMenuAPI.getAll();
      console.log('API Response:', response);
      console.log('Response data:', response.data);
      
      // S'assurer que nous avons un tableau
      if (Array.isArray(response.data)) {
        setCategories(response.data);
      } else if (response.data && typeof response.data === 'object') {
        // Si l'API retourne un objet avec une propriété contenant le tableau
        const possibleArrayProps = ['results', 'data', 'items', 'categories'];
        for (const prop of possibleArrayProps) {
          if (Array.isArray(response.data[prop])) {
            setCategories(response.data[prop]);
            return;
          }
        }
        // Si on ne trouve pas de tableau, on met un tableau vide
        console.warn('API ne retourne pas un tableau:', response.data);
        setCategories([]);
        setError('Format de données inattendu de l\'API');
      } else {
        setCategories([]);
        setError('Format de données invalide');
      }
    } catch (error) {
      console.error('Erreur détaillée:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Erreur lors du chargement des catégories';
      setError(errorMessage);
      toast.error(errorMessage);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const resetForm = () => {
    setFormData({
      nom: '',
      type_categorie: 'entree',
      description: '',
      est_active: true
    });
    setIsEditing(false);
  };

  const handleOpenDialog = (categorie = null) => {
    if (categorie) {
      setFormData(categorie);
      setIsEditing(true);
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        await categorieMenuAPI.update(formData.id, formData);
        toast.success('Catégorie modifiée avec succès');
      } else {
        await categorieMenuAPI.create(formData);
        toast.success('Catégorie créée avec succès');
      }
      setOpenDialog(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.nom?.[0] || 
                          error.response?.data?.detail || 
                          error.message ||
                          'Erreur lors de l\'enregistrement';
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        await categorieMenuAPI.delete(id);
        toast.success('Catégorie supprimée avec succès');
        fetchCategories();
      } catch (error) {
        console.error('Erreur:', error);
        const errorMessage = error.response?.data?.detail || 
                            error.message ||
                            'Erreur lors de la suppression';
        toast.error(errorMessage);
      }
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      await categorieMenuAPI.update(id, { est_active: !currentStatus });
      toast.success(`Catégorie ${!currentStatus ? 'activée' : 'désactivée'}`);
      fetchCategories();
    } catch (error) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message ||
                          'Erreur lors de la modification du statut';
      toast.error(errorMessage);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      entree: 'primary',
      plat_principal: 'success',
      dessert: 'warning',
      accompagnement: 'info',
      sauce: 'error',
      salade: 'success',
      soupe: 'secondary'
    };
    return colors[type] || 'default';
  };

  // Fonction utilitaire pour vérifier si categories est un tableau
  const getCategoriesArray = () => {
    if (!Array.isArray(categories)) {
      console.warn('categories n\'est pas un tableau:', categories);
      return [];
    }
    return categories;
  };

  const categoriesArray = getCategoriesArray();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
        p={3} // Ajout de padding
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert 
          severity="error" 
          icon={<Warning />}
          action={
            <Button color="inherit" size="small" onClick={fetchCategories}>
              Réessayer
            </Button>
          }
        >
          <Typography variant="h6" gutterBottom>
            Erreur de chargement
          </Typography>
          <Typography>
            {error}
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              Détails de debug:
              <br />
              Type de categories: {typeof categories}
              <br />
              Est un tableau: {Array.isArray(categories) ? 'Oui' : 'Non'}
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      sx={{
        p: 3, // Padding général de 24px
        backgroundColor: 'background.default', // Optionnel: couleur de fond cohérente
        minHeight: '100vh', // Prend toute la hauteur
      }}
    >
      {/* Header avec statistiques */}
      <Box mb={4}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" gutterBottom fontWeight="bold">
              <RestaurantMenu sx={{ mr: 2, verticalAlign: 'middle' }} />
              Catégories Menu
            </Typography>
            <Typography color="textSecondary" variant="body1">
              Gérez les différentes catégories de plats pour votre menu
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ 
                height: 48,
                px: 3,
                borderRadius: 1
              }}
            >
              Nouvelle Catégorie
            </Button>
          </Grid>
        </Grid>

        {/* Cartes statistiques */}
        <Grid container spacing={3} mt={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Total Catégories
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {categoriesArray.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Actives
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {categoriesArray.filter(c => c.est_active).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Entrées
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {categoriesArray.filter(c => c.type_categorie === 'entree').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: 2,
              height: '100%'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="subtitle2">
                  Plats Principaux
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {categoriesArray.filter(c => c.type_categorie === 'plat_principal').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Tableau des catégories */}
      <Paper 
        elevation={2} 
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Statut</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Date Création</TableCell>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categoriesArray.length > 0 ? (
                categoriesArray.map((cat) => (
                  <TableRow key={cat.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ py: 2 }}>
                      <Typography fontWeight="medium">{cat.nom}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={typeOptions.find(t => t.value === cat.type_categorie)?.label || cat.type_categorie}
                        color={getTypeColor(cat.type_categorie)}
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                        {cat.description || 'Aucune description'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={cat.est_active}
                              onChange={() => toggleStatus(cat.id, cat.est_active)}
                              size="small"
                            />
                          }
                          label={
                            <Chip
                              icon={cat.est_active ? <Visibility /> : <VisibilityOff />}
                              label={cat.est_active ? 'Active' : 'Inactive'}
                              size="small"
                              color={cat.est_active ? 'success' : 'default'}
                              variant="outlined"
                              sx={{ borderRadius: 1 }}
                            />
                          }
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2">
                        {cat.date_creation ? new Date(cat.date_creation).toLocaleDateString('fr-FR') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }} align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenDialog(cat)}
                          size="small"
                          sx={{ 
                            '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(cat.id)}
                          size="small"
                          sx={{ 
                            '&:hover': { backgroundColor: 'error.light', color: 'white' }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <RestaurantMenu sx={{ fontSize: 60, color: 'text.disabled' }} />
                      <Typography variant="body1" color="textSecondary">
                        Aucune catégorie trouvée
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Créez votre première catégorie pour commencer
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{ mt: 1 }}
                      >
                        Créer une catégorie
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog d'édition/création */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <RestaurantMenu />
            <Typography variant="h6">
              {isEditing ? 'Modifier la Catégorie' : 'Nouvelle Catégorie'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            <TextField
              label="Nom de la catégorie"
              fullWidth
              value={formData.nom}
              onChange={(e) => setFormData({...formData, nom: e.target.value})}
              required
              error={!formData.nom}
              helperText={!formData.nom ? 'Ce champ est requis' : ''}
              variant="outlined"
              size="small"
            />
            
            <TextField
              select
              label="Type de catégorie"
              fullWidth
              value={formData.type_categorie}
              onChange={(e) => setFormData({...formData, type_categorie: e.target.value})}
              variant="outlined"
              size="small"
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            
            <TextField
              label="Description"
              multiline
              rows={4}
              fullWidth
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Décrivez cette catégorie..."
              variant="outlined"
              size="small"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.est_active}
                  onChange={(e) => setFormData({...formData, est_active: e.target.checked})}
                />
              }
              label={
                <Typography variant="body2">
                  Catégorie active
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            variant="outlined"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.nom}
            sx={{ ml: 1 }}
          >
            {isEditing ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesMenuPage;